/**
 * Create Organization Page
 * Shown when a user doesn't have any organization
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptIcon from '@mui/icons-material/Receipt';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';
import { useCreateOrganization } from '../hooks/useOrganizations';

const steps = ['Βασικά Στοιχεία', 'Στοιχεία Επικοινωνίας', 'Ολοκλήρωση'];

export default function CreateOrganizationPage() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    vat_number: '',
    email: '',
    phone: '',
    address: '',
  });
  const [errors, setErrors] = useState({});
  
  const createOrg = useCreateOrganization();

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 0) {
      if (!formData.name.trim()) {
        newErrors.name = 'Το όνομα είναι υποχρεωτικό';
      } else if (formData.name.trim().length < 2) {
        newErrors.name = 'Το όνομα πρέπει να έχει τουλάχιστον 2 χαρακτήρες';
      }
    }
    
    if (step === 1) {
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Μη έγκυρο email';
      }
      if (formData.phone && !/^[0-9+\-\s()]{8,}$/.test(formData.phone)) {
        newErrors.phone = 'Μη έγκυρο τηλέφωνο';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(1)) return;
    
    try {
      await createOrg.mutateAsync(formData);
      setActiveStep(2); // Success step
      
      // Redirect after 2 seconds
      setTimeout(() => {
        window.location.href = '/'; // Full reload to refresh org context
      }, 2000);
    } catch (error) {
      setErrors({ submit: error.message });
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Πληροφορίες Επιχείρησης
            </Typography>
            
            <TextField
              label="Όνομα Επιχείρησης"
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name || 'π.χ. Καφετέρια Ο Κώστας'}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              label="ΑΦΜ (προαιρετικό)"
              value={formData.vat_number}
              onChange={handleChange('vat_number')}
              error={!!errors.vat_number}
              helperText={errors.vat_number || 'Για σύνδεση με myDATA'}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ReceiptIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        );
        
      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Στοιχεία Επικοινωνίας
            </Typography>
            
            <TextField
              label="Email (προαιρετικό)"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              label="Τηλέφωνο (προαιρετικό)"
              value={formData.phone}
              onChange={handleChange('phone')}
              error={!!errors.phone}
              helperText={errors.phone}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              label="Διεύθυνση (προαιρετικό)"
              value={formData.address}
              onChange={handleChange('address')}
              multiline
              rows={2}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                    <LocationOnIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        );
        
      case 2:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
              Επιτυχής Δημιουργία!
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Η επιχείρηση "{formData.name}" δημιουργήθηκε επιτυχώς.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ανακατεύθυνση σε 2 δευτερόλεπτα...
            </Typography>
          </Box>
        );
        
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 500,
          width: '100%',
          p: 4,
          borderRadius: 3,
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              mb: 1,
            }}
          >
            Palmera
          </Typography>
          <Typography color="text.secondary">
            Δημιουργήστε την επιχείρησή σας
          </Typography>
        </Box>

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error Alert */}
        {errors.submit && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errors.submit}
          </Alert>
        )}

        {/* Step Content */}
        {renderStepContent(activeStep)}

        {/* Navigation Buttons */}
        {activeStep < 2 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              sx={{ visibility: activeStep === 0 ? 'hidden' : 'visible' }}
            >
              Πίσω
            </Button>
            
            {activeStep === 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={createOrg.isPending}
                sx={{
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' },
                  px: 4,
                }}
              >
                {createOrg.isPending ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Δημιουργία'
                )}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' },
                  px: 4,
                }}
              >
                Επόμενο
              </Button>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

